import * as React from 'react';

import { Button } from '@gearbelt/ui/components';

import { cn } from '@/lib/utils';

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './tooltip';

interface ButtonStateToggleOption<T = string> {
  icon?: React.ReactNode;
  label: string;
  value: T;
}

interface ButtonStateToggleProps<T = string> {
  options: ButtonStateToggleOption<T>[];
  value: T;
  onChange: (value: T) => void;
  className?: string;
  responsive?: boolean;
}

const ButtonStateToggle = <T,>({ options, value, onChange, className, responsive = false }: ButtonStateToggleProps<T>) => {
  if (options.length < 2) {
    throw new Error('ButtonStateToggle requires at least 2 options');
  }

  return (
    <div className="inline-flex rounded-md">
      {options.map((option, index) => (
        <TooltipProvider key={index} delayDuration={300}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant={value === option.value ? 'default' : 'grey'}
                className={cn(
                  // Common
                  'rounded px-2 py-1',
                  // First button
                  index === 0 && 'rounded-r-none',
                  // Middle buttons
                  index > 0 && index < options.length - 1 && 'rounded-none border-l-0',
                  // Last button
                  index === options.length - 1 && 'rounded-l-none border-l-0',
                  className,
                )}
                type="button"
                aria-pressed={value === option.value}
                onClick={() => onChange(option.value)}>
                {option.icon && <span className={responsive ? '3xl:mr-1 mr-0' : 'mr-1'}>{option.icon}</span>}
                <span className={responsive ? '3xl:w-auto w-0 overflow-hidden' : 'inline'}>{option.label}</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent className={responsive ? '3xl:hidden' : 'hidden'}>{option.label}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ))}
    </div>
  );
};

export { ButtonStateToggle, type ButtonStateToggleOption };
